/**
 * /campaigns/:id/assets — the campaign's asset library.
 *
 * Lifted from the old /assets page; scoped to the campaign in the
 * URL, no picker needed. Upload form + delete + type filter all
 * preserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, apiJson } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { useCampaignContext } from '../CampaignDetail';
import { CAMPAIGN_TEMPLATES } from '../../lib/campaignTemplates';
import { ScreenshotsResync } from '../../components/ScreenshotsResync';

const ASSET_TYPES = ['photo', 'clip', 'screenshot', 'testimonial', 'logo', 'music'] as const;
type AssetType = (typeof ASSET_TYPES)[number];

// Provenance values — mirror the CHECK constraint added in
// migration 109. The filter chip set + per-card badge below
// iterates over this list, so adding a new source value here is
// the only client-side change needed when extending the taxonomy.
const ASSET_SOURCES = ['upload', 'pexels', 'generated', 'tripcode_app', 'higgsfield'] as const;
type AssetSource = (typeof ASSET_SOURCES)[number];

const SOURCE_LABEL: Record<AssetSource, string> = {
  upload: 'Upload',
  pexels: 'Pexels',
  generated: 'AI',
  tripcode_app: 'App',
  higgsfield: 'Higgsfield',
};

interface Asset {
  id: string;
  // Nullable: NULL = global asset (visible to every campaign).
  campaign_id: string | null;
  type: AssetType;
  source: AssetSource;
  r2_key: string | null;
  tags: string[];
  description: string;
  filename: string | null;
  size_bytes: number | null;
  mime_type: string | null;
  created_at: string;
  public_url: string | null;
}

// Strip vs grid split. After the global-pool refactor an asset goes
// in the strip when:
//   - it has no campaign (campaign_id === null) → live in the global
//     pool, e.g. the tripcode app screenshot pack, OR
//   - it's a screenshot uploaded for THIS campaign specifically
//     (admin's own product captures).
// Everything else (photos, clips, etc.) goes into the main grid.
function isAppScreenshot(a: Asset): boolean {
  return a.campaign_id === null || a.type === 'screenshot';
}

function extractInitialSelection(brandSettings: Record<string, unknown>): Set<string> {
  const raw = brandSettings?.selected_asset_ids;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((v): v is string => typeof v === 'string' && v.length > 0));
}

export function AssetsTab() {
  const { campaign, refreshJobs } = useCampaignContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = (searchParams.get('type') as AssetType | null) ?? null;
  // Source filter is client-side (filter applied below) so we don't
  // need a server-side query param + RPC change just for this.
  // The page already loads up to 100 assets — filtering 100 rows
  // in-browser is instant. URL-backed so a "show me the auto-pulled
  // Pexels rows" link is shareable.
  const sourceFilter = (searchParams.get('source') as AssetSource | null) ?? null;
  // Two sources for "which template was this campaign created from":
  //   1. ?seed_from=<id> in URL — set immediately after create
  //   2. campaign.brand_settings.template_id — persisted on the campaign
  //      row so navigating away and back still surfaces the banner
  // URL wins if both are present (someone passed it explicitly), but
  // brand_settings is the long-lived source.
  const seedFromUrl = searchParams.get('seed_from');
  const brandTemplateId =
    typeof campaign.brand_settings?.template_id === 'string'
      ? (campaign.brand_settings.template_id as string)
      : null;
  const effectiveTemplateId = seedFromUrl ?? brandTemplateId;
  const seedTemplate = effectiveTemplateId
    ? CAMPAIGN_TEMPLATES.find((t) => t.id === effectiveTemplateId)
    : null;

  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Selection — controls which asset_ids the matcher considers when
  // briefs are submitted or re-matched. Empty set = no filter (every
  // asset eligible). Persists to campaign.brand_settings.selected_asset_ids.
  const [selection, setSelection] = useState<Set<string>>(() =>
    extractInitialSelection(campaign.brand_settings),
  );
  const [savingSelection, setSavingSelection] = useState(false);
  const [selectionSaveError, setSelectionSaveError] = useState<string | null>(null);

  async function persistSelection(next: Set<string>) {
    setSavingSelection(true);
    setSelectionSaveError(null);
    try {
      const nextBrand = {
        ...(campaign.brand_settings ?? {}),
        selected_asset_ids: Array.from(next),
      };
      const { error: updateError } = await supabase
        .from('marketing_campaigns')
        .update({ brand_settings: nextBrand })
        .eq('id', campaign.id);
      if (updateError) throw new Error(updateError.message);
      // Refresh campaign context so the next /brief or /match-assets
      // call sees the updated selection too.
      await refreshJobs();
    } catch (e) {
      setSelectionSaveError(e instanceof Error ? e.message : 'Selection save failed');
    } finally {
      setSavingSelection(false);
    }
  }

  function toggleAsset(id: string) {
    setSelection((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      void persistSelection(next);
      return next;
    });
  }

  function selectAll() {
    if (!assets) return;
    const next = new Set(assets.map((a) => a.id));
    setSelection(next);
    void persistSelection(next);
  }

  function clearSelection() {
    setSelection(new Set());
    void persistSelection(new Set());
  }

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setAssets(null);
    const params = new URLSearchParams({ campaign_id: campaign.id, limit: '100' });
    if (typeFilter) params.set('type', typeFilter);
    apiJson<{ assets: Asset[] }>(`/api/marketing/assets?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setAssets(res.assets);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load assets');
        setAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.id, typeFilter, refreshKey]);

  function patchType(value: AssetType | null) {
    setSearchParams((sp) => {
      if (value === null) sp.delete('type');
      else sp.set('type', value);
      return sp;
    });
  }

  function patchSource(value: AssetSource | null) {
    setSearchParams((sp) => {
      if (value === null) sp.delete('source');
      else sp.set('source', value);
      return sp;
    });
  }

  // Apply the client-side source filter before handing the list off
  // to the section splitters. Doing it here keeps the unfiltered
  // count off `assets` available for the selection toolbar's "select
  // all" affordance — selection still operates on the full set,
  // filter just changes which rows render.
  const visibleAssets = sourceFilter
    ? assets?.filter((a) => a.source === sourceFilter) ?? null
    : assets;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <label className="field" style={{ flex: '0 0 200px' }}>
          <span>Type</span>
          <select
            value={typeFilter ?? ''}
            onChange={(e) => patchType((e.target.value as AssetType) || null)}
          >
            <option value="">All</option>
            {ASSET_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flex: '0 0 200px' }}>
          <span>Source</span>
          <select
            value={sourceFilter ?? ''}
            onChange={(e) => patchSource((e.target.value as AssetSource) || null)}
          >
            <option value="">All</option>
            {ASSET_SOURCES.map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <ScreenshotsResync onSeeded={bumpRefresh} />

      {seedTemplate && (
        <SeedSuggestions
          campaignId={campaign.id}
          templateLabel={seedTemplate.label}
          queries={seedTemplate.seed_queries}
          onSeeded={() => {
            // After auto-seed finishes, refresh the grid and remove
            // the seed_from param so the banner doesn't re-appear on
            // tab navigation.
            bumpRefresh();
            setSearchParams((sp) => {
              sp.delete('seed_from');
              return sp;
            });
          }}
          onDismiss={() =>
            setSearchParams((sp) => {
              sp.delete('seed_from');
              return sp;
            })
          }
        />
      )}

      <UploadForm campaignId={campaign.id} campaignName={campaign.name} onUploaded={bumpRefresh} />

      {assets && assets.length > 0 && (
        <SelectionToolbar
          totalCount={assets.length}
          selectedCount={selection.size}
          saving={savingSelection}
          saveError={selectionSaveError}
          onSelectAll={selectAll}
          onClear={clearSelection}
        />
      )}

      <ScreenshotsSection
        assets={visibleAssets}
        selection={selection}
        onToggle={toggleAsset}
        onDeleted={bumpRefresh}
      />

      <OtherAssetsSection
        assets={visibleAssets}
        selection={selection}
        onToggle={toggleAsset}
        onDeleted={bumpRefresh}
      />
    </div>
  );
}

// ── seed suggestions banner ────────────────────────────────────────

/**
 * Shown when arriving on the Assets tab from a template — surfaces
 * the template's curated search queries as clickable links opening
 * Unsplash and Pexels search in new tabs. Admin reviews + downloads
 * + uploads via the form below. Pure UI; no server call.
 *
 * Path B (one-click auto-seed via Pexels API) is planned as part of
 * sub-2.7. Until then, this gives admins a friction-light path to a
 * good starter library.
 */
function SeedSuggestions({
  campaignId,
  templateLabel,
  queries,
  onSeeded,
  onDismiss,
}: {
  campaignId: string;
  templateLabel: string;
  queries: string[];
  onSeeded: () => void;
  onDismiss: () => void;
}) {
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{
    seeded: number;
    photos_seeded?: number;
    videos_seeded?: number;
    errors: { query: string; message: string }[];
  } | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);

  async function autoSeed() {
    setSeeding(true);
    setSeedError(null);
    setSeedResult(null);
    try {
      const res = await apiJson<{
        seeded: number;
        photos_seeded: number;
        videos_seeded: number;
        errors: { query: string; message: string }[];
      }>(
        `/api/marketing/campaigns/${campaignId}/seed-assets`,
        {
          method: 'POST',
          body: JSON.stringify({ queries, per_query: 3, videos_per_query: 1 }),
        },
      );
      setSeedResult(res);
      if (res.seeded > 0) onSeeded();
    } catch (e) {
      setSeedError(e instanceof Error ? e.message : 'Seed failed');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div
      style={{
        background: 'rgba(110, 168, 254, 0.08)',
        border: '1px solid rgba(110, 168, 254, 0.35)',
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div>
          <strong style={{ fontSize: 14 }}>
            Seed suggestions for <em>{templateLabel}</em>
          </strong>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
            One-click auto-seed via Pexels (free, commercial-use license) — or use the per-query
            search links to pick photos yourself from Unsplash / Pexels.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onDismiss}
          aria-label="Dismiss seed suggestions"
          title="Hide these suggestions"
          disabled={seeding}
        >
          ✕
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={seeding}
          onClick={autoSeed}
        >
          {seeding
            ? `Seeding ~${queries.length * 3} photos + ${queries.length} videos…`
            : `Auto-seed ${queries.length * 3} photos + ${queries.length} videos from Pexels`}
        </button>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          ~25 seconds. Photos appear in the grid below as the seed completes.
        </span>
      </div>

      {seedError && (
        <div className="alert alert-error" style={{ marginTop: 8, fontSize: 12 }}>
          {seedError}
        </div>
      )}
      {seedResult && (
        <div
          className={seedResult.seeded > 0 ? 'alert' : 'alert alert-error'}
          style={{ marginTop: 8, fontSize: 12 }}
        >
          Seeded <strong>{seedResult.photos_seeded ?? seedResult.seeded}</strong> photo
          {(seedResult.photos_seeded ?? seedResult.seeded) === 1 ? '' : 's'}
          {typeof seedResult.videos_seeded === 'number'
            ? ` + ${seedResult.videos_seeded} video${seedResult.videos_seeded === 1 ? '' : 's'}`
            : ''}
          .
          {seedResult.errors.length > 0 && (
            <>
              {' '}
              <strong>{seedResult.errors.length}</strong> errors:
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {seedResult.errors.slice(0, 5).map((err, i) => (
                  <li key={i}>
                    <code>{err.query}</code>: {err.message}
                  </li>
                ))}
                {seedResult.errors.length > 5 && (
                  <li>… and {seedResult.errors.length - 5} more</li>
                )}
              </ul>
            </>
          )}
        </div>
      )}

      <details style={{ marginTop: 12 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Or pick photos yourself ({queries.length} curated searches)
        </summary>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 8,
            marginTop: 8,
          }}
        >
          {queries.map((q) => (
            <div
              key={q}
              style={{
                background: 'var(--color-surface-alt)',
                borderRadius: 8,
                padding: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 12, fontFamily: 'inherit' }}>{q}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <a
                  href={`https://unsplash.com/s/photos/${encodeURIComponent(q)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  style={{ flex: 1, textAlign: 'center', fontSize: 11 }}
                >
                  Unsplash ↗
                </a>
                <a
                  href={`https://www.pexels.com/search/${encodeURIComponent(q)}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  style={{ flex: 1, textAlign: 'center', fontSize: 11 }}
                >
                  Pexels ↗
                </a>
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

// ── upload form ────────────────────────────────────────────────────

function UploadForm({
  campaignId,
  campaignName,
  onUploaded,
}: {
  campaignId: string;
  campaignName: string;
  onUploaded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<AssetType>('photo');
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function reset() {
    setType('photo');
    setTags('');
    setDescription('');
    setFile(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function submit() {
    if (!file) {
      setError('Pick a file first');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('campaign_id', campaignId);
      form.append('type', type);
      form.append('tags', tags);
      form.append('description', description);
      const res = await apiFetch('/api/marketing/assets', { method: 'POST', body: form });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${text || 'upload_failed'}`);
      }
      reset();
      setOpen(false);
      onUploaded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      style={{
        background: 'var(--color-surface-alt)',
        border: '1px solid transparent',
        borderRadius: 10,
        padding: '10px 14px',
      }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
        Upload to <strong>{campaignName}</strong>
      </summary>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        <label className="field">
          <span>File (≤ 95 MB)</span>
          <input
            ref={fileRef}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            accept="image/*,video/*,audio/*,.avif,.heic,.heif,.webp"
          />
          {file && (
            <small style={{ color: 'var(--color-text-muted)' }}>
              {file.name} · {formatBytes(file.size)} · {file.type || 'unknown type'}
            </small>
          )}
        </label>

        <div className="brief-grid">
          <label className="field">
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value as AssetType)}>
              {ASSET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Tags (comma-separated)</span>
            <input
              type="text"
              placeholder="outdoor, golden-hour, group, smiling"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </label>
        </div>

        <label className="field">
          <span>Description (used by the matcher)</span>
          <textarea
            rows={3}
            value={description}
            placeholder='"[type]: [subject], [context], [mood/style]" — e.g. "photo: group of friends planning a trip on a laptop, indoor cafe, warm golden hour lighting"'
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        {error && <div className="alert alert-error">{error}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={submitting || !file}
            onClick={submit}
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={submitting}
            onClick={reset}
          >
            Reset
          </button>
        </div>
      </div>
    </details>
  );
}

// ── asset card ─────────────────────────────────────────────────────

function AssetCard({
  asset,
  selected,
  onToggle,
  onDeleted,
  variant = 'grid',
}: {
  asset: Asset;
  selected: boolean;
  onToggle: () => void;
  onDeleted: () => void;
  variant?: 'grid' | 'strip';
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleCardClick(e: React.MouseEvent<HTMLElement>) {
    // Inner controls (delete button, preview audio/video) shouldn't
    // toggle selection. We check the closest interactive ancestor —
    // anything tagged data-asset-control is opt-out.
    const target = e.target as HTMLElement;
    if (target.closest('[data-asset-control]')) return;
    onToggle();
  }

  async function del() {
    const purge = window.confirm(
      `Delete "${asset.filename ?? asset.id}"?\n\n` +
        'OK = soft-delete + purge bytes from R2 (storyboards referencing it will fall back to solid color).\n' +
        'Cancel = leave it.',
    );
    if (!purge) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/marketing/assets/${asset.id}?purge=1`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${text || 'delete_failed'}`);
      }
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setBusy(false);
    }
  }

  // Strip cards match the main grid's minimum cell width so the two
  // surfaces look identical visually. The strip is horizontally
  // scrollable so width can be fixed without breaking layout.
  const stripStyle: React.CSSProperties =
    variant === 'strip'
      ? { width: 240, flexShrink: 0, scrollSnapAlign: 'start' }
      : {};

  return (
    <article
      className="card"
      onClick={handleCardClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        cursor: 'pointer',
        // Selected state — primary-color border so it's obvious which
        // assets the matcher will consider. The "selected" set is the
        // matcher's allowlist; when empty, no card has this border.
        border: selected
          ? '2px solid var(--color-primary, #E8432D)'
          : '2px solid transparent',
        outline: 'none',
        transition: 'border-color 100ms',
        ...stripStyle,
      }}
      role="button"
      aria-pressed={selected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <AssetPreview asset={asset} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <strong style={{ fontSize: 13, wordBreak: 'break-word' }}>
          {asset.filename ?? asset.id.slice(0, 8)}
        </strong>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {/*
            Source badge sits left of the type pill so the auto-pulled
            rows (Pexels / generated) are immediately spottable when
            curating. Plain text labels — the variety of sources is
            small enough that 3-letter codes (UPL/PEX/AI/APP) wouldn't
            save real estate worth the legibility hit.
          */}
          <span
            style={{
              fontSize: 11,
              background:
                asset.source === 'generated'
                  ? 'rgba(232, 67, 45, 0.15)'
                  : asset.source === 'pexels'
                  ? 'rgba(96, 165, 250, 0.15)'
                  : 'var(--color-surface-alt)',
              padding: '2px 6px',
              borderRadius: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-text-muted)',
            }}
            title={`Source: ${asset.source}`}
          >
            {SOURCE_LABEL[asset.source] ?? asset.source}
          </span>
          <span
            style={{
              fontSize: 11,
              background: 'var(--color-surface-alt)',
              padding: '2px 6px',
              borderRadius: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-text-muted)',
            }}
          >
            {asset.type}
          </span>
        </div>
      </div>
      {asset.description && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
          {asset.description}
        </p>
      )}
      {asset.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {asset.tags.map((t) => (
            <span
              key={t}
              style={{
                fontSize: 11,
                background: 'var(--color-surface-alt)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      )}
      <small style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
        {asset.size_bytes ? formatBytes(asset.size_bytes) : ''} ·{' '}
        {new Date(asset.created_at).toLocaleDateString()}
      </small>
      {error && (
        <div className="alert alert-error" style={{ fontSize: 11 }}>
          {error}
        </div>
      )}
      <button
        type="button"
        data-asset-control
        className="btn btn-ghost btn-sm"
        disabled={busy}
        onClick={del}
      >
        {busy ? 'Deleting…' : 'Delete'}
      </button>
    </article>
  );
}

// ── selection toolbar ─────────────────────────────────────────────

function SelectionToolbar({
  totalCount,
  selectedCount,
  saving,
  saveError,
  onSelectAll,
  onClear,
}: {
  totalCount: number;
  selectedCount: number;
  saving: boolean;
  saveError: string | null;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const noFilter = selectedCount === 0;
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        flexWrap: 'wrap',
        background: 'var(--color-surface-alt)',
        borderRadius: 10,
        padding: '8px 14px',
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 600 }}>
        {noFilter
          ? `Matcher will consider all ${totalCount} asset${totalCount === 1 ? '' : 's'}`
          : `${selectedCount} of ${totalCount} selected for matching`}
      </span>
      <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
        {noFilter
          ? 'Click any asset to start curating.'
          : 'Only highlighted assets will be used when generating or re-matching variants.'}
      </span>
      <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onSelectAll} disabled={saving}>
          Select all
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onClear}
          disabled={saving || noFilter}
          title="Clear selection — matcher reverts to considering every asset"
        >
          Clear
        </button>
      </div>
      {saving && (
        <small style={{ color: 'var(--color-text-muted)', fontSize: 11, marginLeft: 8 }}>
          Saving selection…
        </small>
      )}
      {saveError && (
        <small style={{ color: 'crimson', fontSize: 11, marginLeft: 8 }}>{saveError}</small>
      )}
    </div>
  );
}

// ── screenshots horizontal strip ──────────────────────────────────

function ScreenshotsSection({
  assets,
  selection,
  onToggle,
  onDeleted,
}: {
  assets: Asset[] | null;
  selection: Set<string>;
  onToggle: (id: string) => void;
  onDeleted: () => void;
}) {
  const screenshots = useMemo(
    () => (assets ? assets.filter(isAppScreenshot) : []),
    [assets],
  );

  if (assets === null || screenshots.length === 0) return null;

  return (
    <section style={{ marginTop: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 8,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>tripcode app screenshots</h3>
        <small style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
          {screenshots.length} screen{screenshots.length === 1 ? '' : 's'} · re-seedable from the
          link above
        </small>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          paddingBottom: 8,
          scrollSnapType: 'x mandatory',
          // subtle visual separation from the main grid below
          borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.08))',
        }}
      >
        {screenshots.map((asset) => (
          <AssetCard
            key={asset.id}
            asset={asset}
            selected={selection.has(asset.id)}
            onToggle={() => onToggle(asset.id)}
            onDeleted={onDeleted}
            variant="strip"
          />
        ))}
      </div>
    </section>
  );
}

// ── main grid (uploads + Pexels seeds + everything else) ──────────

function OtherAssetsSection({
  assets,
  selection,
  onToggle,
  onDeleted,
}: {
  assets: Asset[] | null;
  selection: Set<string>;
  onToggle: (id: string) => void;
  onDeleted: () => void;
}) {
  const others = useMemo(
    () => (assets ? assets.filter((a) => !isAppScreenshot(a)) : []),
    [assets],
  );

  return (
    <section style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 12, fontSize: 16 }}>
        {assets === null
          ? 'Loading…'
          : `${others.length} campaign asset${others.length === 1 ? '' : 's'}`}
      </h3>
      {assets && others.length === 0 && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: 24,
            color: 'var(--color-text-muted)',
          }}
        >
          No campaign assets yet. Upload above, or seed from a template / Pexels.
        </div>
      )}
      {others.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 16,
          }}
        >
          {others.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              selected={selection.has(asset.id)}
              onToggle={() => onToggle(asset.id)}
              onDeleted={onDeleted}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function AssetPreview({ asset }: { asset: Asset }) {
  if (!asset.public_url) return <PreviewPlaceholder label={asset.type} />;
  const mime = asset.mime_type ?? '';
  if (mime.startsWith('image/')) {
    return (
      <img
        src={asset.public_url}
        alt={asset.description || asset.filename || asset.id}
        style={{
          width: '100%',
          aspectRatio: '4 / 5',
          objectFit: 'cover',
          borderRadius: 8,
          background: '#000',
        }}
      />
    );
  }
  if (mime.startsWith('video/')) {
    return (
      <video
        src={asset.public_url}
        muted
        playsInline
        controls
        style={{
          width: '100%',
          aspectRatio: '4 / 5',
          objectFit: 'cover',
          borderRadius: 8,
          background: '#000',
        }}
      />
    );
  }
  if (mime.startsWith('audio/')) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <audio src={asset.public_url} controls style={{ width: '100%' }} />
    );
  }
  return <PreviewPlaceholder label={asset.type} />;
}

function PreviewPlaceholder({ label }: { label: string }) {
  return (
    <div
      style={{
        width: '100%',
        aspectRatio: '4 / 5',
        background: 'var(--color-surface-alt)',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontSize: 12,
      }}
    >
      {label}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
